/**
 * PSR Phase 2 — county GIS / ArcGIS Hub layer discovery.
 */
import { fetchWithTimeout } from '../dataProviders/types';
import { factToCitationRef } from '../../lib/sentiment/citationRefs';
import { resolveSentimentSiteContext } from './siteContext';
import {
  emptySentimentResult,
  type SentimentFetchOptions,
  type SentimentFetchResult,
  type SentimentFetcher,
} from './types';
import { probeHttpAvailability } from './probeAvailability';
import { rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';

const MAX_LAYERS = 5;

async function searchArcGisHub(
  query: string,
  signal?: AbortSignal,
): Promise<Array<{ title: string; url: string }>> {
  const url = `https://hub.arcgis.com/api/search/v1/collections/dataset/items?filter=keywords:${encodeURIComponent(query)}&limit=${MAX_LAYERS}`;
  const res = await fetchWithTimeout(url, 12_000, signal);
  if (!res.ok) return [];
  const json = await res.json() as {
    features?: Array<{ properties?: { title?: string; landingPage?: string; url?: string } }>;
  };
  const out: Array<{ title: string; url: string }> = [];
  for (const f of json.features ?? []) {
    const title = f.properties?.title?.trim();
    const landing = f.properties?.landingPage ?? f.properties?.url;
    if (title && landing) out.push({ title, url: landing });
  }
  return out;
}

export const localGisFetcher: SentimentFetcher = {
  sourceId: 'local-gis',

  async probeAvailability() {
    return probeHttpAvailability({
      url: 'https://hub.arcgis.com/api/search/v1/collections/dataset/items?limit=1',
      unconfiguredStatuses: [401, 403],
    }); // R-130-10
  },

  async isAvailable(siteId, opts) {
    const ctx = await resolveSentimentSiteContext(siteId, opts);
    return !!(ctx?.county || ctx?.locality || ctx?.state);
  },

  async fetch(siteId, opts?: SentimentFetchOptions): Promise<SentimentFetchResult> {
    throwIfAborted(opts?.signal);
    try {
      const ctx = await resolveSentimentSiteContext(siteId, opts);
      if (!ctx) {
        return emptySentimentResult('local-gis', ['local-gis: no site context']);
      }
      const place = ctx.county ?? ctx.locality ?? ctx.state;
      if (!place) {
        return emptySentimentResult('local-gis', ['local-gis: no county/locality seed']);
      }

      const layers = await searchArcGisHub(`${place} zoning land use`, opts?.signal);
      throwIfAborted(opts?.signal);
      if (layers.length === 0) {
        return emptySentimentResult('local-gis', ['local-gis: no GIS layers matched']);
      }

      const now = new Date().toISOString();
      const facts = layers.slice(0, MAX_LAYERS).map((l, i) => ({
        key: `gis_layer_${i + 1}`,
        label: 'County GIS dataset',
        value: l.title.slice(0, 200),
        url: l.url,
        observedAt: now,
      }));

      return {
        sourceId: 'local-gis',
        coverage: layers.length >= 2 ? 'full' : 'partial',
        normalizedFacts: facts,
        citationRefs: facts.map((f, i) => factToCitationRef(f, 'local-gis', i + 1)),
        errors: [],
        fetchedAt: now,
      };
    } catch (e) {
      rethrowIfAborted(opts?.signal, e);
      return emptySentimentResult('local-gis', [`local-gis: ${e instanceof Error ? e.message : String(e)}`]);
    }
  },
};
